import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { auth, db } from "../../firebase";
import { doc, setDoc, writeBatch, collection, getDoc } from "firebase/firestore";

import axios from "axios";

import Popup from "../Popup";
import Loading from "../Loading";

import styleClasses from "./css/ImportArtistPopup.module.scss";

function ImportUnreleasedArtistPopup({ open, setOpenPopup, apiResults, pushAlbums, setPushAlbums, loading }) {
  const [searchParams]  = useSearchParams();
  const musicParam      = searchParams.get("music") || "unreleased";

  const [active, setActive] = useState(''); // 팝업 열기/닫기

  const [uid, setUid]             = useState(null);
  const [authToken, setAuthToken] = useState("");

  const [selectedResults, setSelectedResults]     = useState([]);
  const [albumData, setAlbumData]                 = useState({});
  const [loadingAlbums, setLoadingAlbums]         = useState({});
  const [errorAlbums, setErrorAlbums]             = useState({});
  const [showAlbumList, setShowAlbumList]         = useState(false);
  const [loadingFetchAlbum, setLoadingFetchAlbum] = useState(false);
  const [showProgress, setShowProgress]           = useState(false);
  const [progress, setProgress]                   = useState(0);

  const [artistChannelMeta, setArtistChannelMeta] = useState({});

  const [openInnerPopup, setOpenInnerPopup]   = useState(false);
  const [innerPopupTitle, setInnerPopupTitle] = useState("");
  const [innerPopupText, setInnerPopupText]   = useState("");

  // 🔹 팝업 - 상태 초기화
  useEffect(() => {
    setActive(open ? styleClasses.active : '');

    if (open) {
      setSelectedResults([]);
      setAlbumData({});
      setLoadingAlbums({});
      setErrorAlbums({});
      setPushAlbums([]);
      setShowAlbumList(false);
    }
  }, [open, setPushAlbums]);

  // 🔹 ID 및 토큰 조회
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUid(user.uid);
        const token = await user.getIdToken();
        setAuthToken(token);
      }
    });
    return () => unsubscribe();
  }, []);

  // 🔹 사운드 클라우드 앨범 조회
  const fetchAlbumsSoundcloud = async (artistId, artistName = '') => {
    setLoadingAlbums((prev) => ({ ...prev, [artistId]: true }));
    try {
      const endpoint = "https://music-api-134893551192.asia-northeast3.run.app/api/artist/soundcloudInfo";
      const response = await axios.get(endpoint, {
        params: { userUrl: artistId },
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return { status: response.status, ...response.data };
    } catch (error) {
      setErrorAlbums((prev) => ({ ...prev, [artistId]: `${ artistName } 사운드클라우드 앨범 데이터를 가져오는 중 오류가 발생했습니다.` }));
      return { albums: [], channelMeta: null };
    } finally {
      setShowAlbumList(true);
      setLoadingAlbums((prev) => ({ ...prev, [artistId]: false }));
    }
  };

  // 🔹 아티스트 선택 및 해제 시 앨범 조회 및 삭제 이벤트
  const handleCheckboxChange = async (result, checked) => {
    if (checked) {
      setLoadingFetchAlbum(true);
      setSelectedResults((prev) => [...prev, result]);
      const { status, albums, channelMeta } = await fetchAlbumsSoundcloud(result.artistId);
      setLoadingFetchAlbum(false);

      if (status === 429) {
        setOpenInnerPopup(true);
        setInnerPopupTitle('사운드 클라우드 요청 제한이 초과되었습니다. <br/>24시간 후에 다시 시도해주세요.');
        setInnerPopupText('');
        return;
      } else if (!albums || albums.length === 0) {
        setOpenInnerPopup(true);
        setInnerPopupTitle("트랙 정보 없음");
        setInnerPopupText("업로드된 트랙이 존재하지 않는 계정입니다.");
        setArtistChannelMeta((prev) => ({ ...prev, [result.artistId]: channelMeta }));
      } else {
        setAlbumData((prev) => ({ ...prev, [result.artistId]: albums }));
        setArtistChannelMeta((prev) => ({ ...prev, [result.artistId]: channelMeta }));
      }
    } else {
      setSelectedResults((prev) => prev.filter((item) => item.artistId !== result.artistId));
      setAlbumData((prev) => {
        const newData = { ...prev };
        delete newData[result.artistId];
        return newData;
      });
      setArtistChannelMeta((prev) => {
        const newData = { ...prev };
        delete newData[result.artistId];
        return newData;
      });
    }
  };

  // 🔹 앨범 단일 선택
  const checkAlbum = (checked, album) => {
    setPushAlbums((prev) => (checked ? [...prev, album] : prev.filter((item) => item.id !== album.id)));
    const safeAlbumCardClass = CSS.escape(styleClasses.albumCard);
    const allAlbums = document.querySelectorAll(`.${safeAlbumCardClass} input[name="eachAlbum"]`);
    const checkedAlbums = document.querySelectorAll(`.${safeAlbumCardClass} input[name="eachAlbum"]:checked`);
    const allCheckBox = document.getElementById("checkAllAlbums");
    if (allAlbums && allCheckBox) allCheckBox.checked = allAlbums.length === checkedAlbums.length;
  };

  // 🔹 앨범 전체 선택
  const checkAllAlbums = (checked) => {
    const safeAlbumCardClass = CSS.escape(styleClasses.albumCard);
    const targetAlbums = document.querySelectorAll(`.${safeAlbumCardClass} input[name="eachAlbum"]`);
  
    if (targetAlbums.length) {
      targetAlbums.forEach((eachAlbum) => (eachAlbum.checked = checked));
  
      const allAlbums = Object.values(albumData).flat(); // 🔥 모든 아티스트의 앨범들을 합침
      setPushAlbums(checked ? allAlbums : []);
    }
  };  

  // 🔹 저장
  const handleComplete = async () => {
    const artistId = selectedResults[0]?.artistId;
    if (!artistId || !uid) return;
    const channelMetaData = artistChannelMeta[artistId];
    const completeProfileImage = channelMetaData?.avatarUrl || "";
    let completeID = "";
    if (channelMetaData?.channelTitle) {
      completeID = channelMetaData.channelTitle
        .trim()                         // 앞뒤 공백 제거
        .replace(/\s+/g, "_")           // 띄어쓰기 → 밑줄
        .replace(/[^A-Za-z0-9_]/g, ""); // 알파벳, 숫자, 밑줄만 허용
    }

    try {
      if (pushAlbums.length > 0) {
        const allItemsLength = pushAlbums.length;
        let currentProgress = 1;
        if (allItemsLength > 2) {
          setShowProgress(true);
          setTimeout(() => {
            setProgress(((currentProgress / allItemsLength) * 100).toFixed(2));
          }, 100);
        }
        for (const album of pushAlbums) {
          const albumRef = doc(db, "users_data", uid, musicParam, album.id.toString());
          const albumDataToSave = {
            cover: album.cover,
            title: album.title,
            year: album.year,
            artist: album.artist,
            description: album.description ?? "",
            createdAt: new Date().toISOString(),
          };
          await setDoc(albumRef, albumDataToSave);
          const updatedTracks = (album.tracks || []).map((track, index) => ({
            id: (index + 1).toString().padStart(2, "0"),
            title: track.title,
            url: track.link,
            playCount: track.playCount ?? 0,
            roles: track.credits?.join(", ") || "",
          }));
          const batch = writeBatch(db);
          const tracksRef = collection(db, "users_data", uid, musicParam, album.id.toString(), "tracks");
          updatedTracks.forEach((track) => {
            const trackDocRef = doc(tracksRef, track.id);
            batch.set(trackDocRef, track);
          });
          await batch.commit();
          if (allItemsLength > 2) {
            currentProgress++;
            const percent = (currentProgress / allItemsLength) * 100;
            setProgress(percent === 100 ? 99 : percent.toFixed(2));
          }
        }

        setProgress(100);
      }

      const timestamp = Date.now(); // 예: 1713187382854
      const contactRef = doc(db, "users_data", uid, "contact", `Initial Contact_${timestamp}`);
      const sns = {};
      if (Array.isArray(channelMetaData.externalLinks)) {
        channelMetaData.externalLinks.forEach((link) => {
          let url = link.url || "";
          
          // ✅ 프로토콜이 없으면 https://를 자동으로 붙임
          if (!/^https?:\/\//i.test(url)) {
            url = `https://${url}`;
          }
    
          sns[link.title] = url;
        });
      }

      await setDoc(contactRef, {
        name: channelMetaData.channelTitle || "",
        introduction: channelMetaData.channelDesc || "",
        sns,
      });

      const userRef = doc(db, "users_data", uid);

      // 1. musicLinks 구성 (SoundCloud만)
      const musicLinks = {
        Soundcloud: channelMetaData.canonicalUrl || ""
      };
      
      // 2. 새로 수집된 이미지들
      const newGallerySet = new Set();
      if (completeProfileImage) newGallerySet.add(completeProfileImage);
      Object.values(pushAlbums).flat().forEach((album) => {
        if (album.cover) newGallerySet.add(album.cover);
      });
      const newGallery = Array.from(newGallerySet);
      
      // 3. 기존 Firestore의 web-gallery와 병합
      let mergedGallery = newGallery; // 기본은 새 목록만
      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const existingGallery = userSnap.data()["web-gallery"];
          if (Array.isArray(existingGallery)) {
            const mergedSet = new Set([...existingGallery, ...newGallery]);
            mergedGallery = Array.from(mergedSet);
          }
        }
      } catch (err) {
        console.warn("기존 web-gallery 병합 중 오류:", err);
      }
      
      // 4. 병합 후 저장
      await setDoc(userRef, {
        musicardId: completeID || '',
        profileImage: completeProfileImage,
        musicLinks,
        "web-gallery": mergedGallery
      }, { merge: true });
      
      setShowProgress(false);
      setProgress(0);
      setOpenPopup(false);
    } catch (error) {
      setProgress(0);
      setShowProgress(false);
      console.error("handleComplete (Unreleased) 에러:", error);
    }
  };

  return (
    <>
      <div className={`${ styleClasses.backDrop } ${ active }`} onClick={ () => { setActive(''); setOpenPopup(false); } }/>
      <div className={`${ styleClasses.importPopup } ${ active }`}>
        <div className={ styleClasses.inner }>
          {/* Header */}
          <div className={ styleClasses.header }>
            <div className={ styleClasses.title }>
              <h1>아티스트 가져오기 (SoundCloud)</h1>
              <p>나와 관련 있는 채널은 모두 선택하고, 넣을 앨범을 선택하세요.</p>
            </div>
            <div className={ styleClasses.closeButton }>
              <button onClick={ () => { setActive(''); setOpenPopup(false); } }>
                <div className="commonIcon">
                  <i className="closeWhite w32"></i>
                </div>
              </button>
            </div>
          </div>

          {/* Content - 아티스트/앨범 결과 */}
          <div className={`${ styleClasses.content } ${ loading ? styleClasses.initializing : undefined }`}>
          {
            loading
            ?
            <Loading />
            :
            <>
              {/* 아티스트 검색 결과 영역 */}
              <div className={ styleClasses.searchedArtists }>
              {
                apiResults && apiResults.length > 0
                ?
                (
                  <ul>
                  {
                    apiResults.map((result) => (
                      <li key={ result.artistId }>
                        <div className="commonForm">
                          <div className="formRow fullWidth">
                            <label htmlFor={`artist-${ result.artistId }`} className="fieldBox checkBox">
                              <input type="checkbox" id={`artist-${ result.artistId }`} onChange={ (e) => handleCheckboxChange(result, e.target.checked) } />
                              <label htmlFor={`artist-${ result.artistId }`}>
                                <img src={ result.thumbnail } alt={ result.artistName } />
                                <p>{ result.artistName }</p>
                                <p>{`${ result.trackCount } 트랙 (${ result.from })`}</p>
                                </label>
                            </label>
                          </div>
                        </div>
                      </li>
                    ))
                  }
                  </ul>
                )
                :
                (
                  <p className={ styleClasses.noResult }>아티스트 검색 결과가 없습니다.</p>
                )
              }
              </div>
              {/* 앨범 목록 출력 영역 */}
              <div className={`${ styleClasses.searchedAlbums } ${ showAlbumList ? styleClasses.active : '' } ${ loadingFetchAlbum ? styleClasses.loading : '' }`}>
              {
                (Object.keys(albumData).length > 0)
                ?
                (
                  <>
                  <div className="commonForm">
                    <div className="formRow">
                      <div className="fieldBox checkBox">
                        <div className={`eachBox ${ styleClasses.noPadding }`}>
                          <input type="checkbox" id="checkAllAlbums" onChange={ (e) => checkAllAlbums(e.target.checked) } />
                          <label htmlFor="checkAllAlbums">모든 앨범 선택</label>
                        </div>
                      </div>
                    </div>
                  </div>
                  {
                    Object.entries(albumData).map(([artistId, albums]) => (
                      <ul key={ artistId }>
                      {
                        loadingAlbums[artistId]
                        ?
                        (
                          <p>앨범 데이터를 가져오는 중...</p>
                        )
                        :
                        errorAlbums[artistId]
                        ?
                        (
                          <p className={ styleClasses.errorAlbum }>{ errorAlbums[artistId] }</p>
                        )
                        :
                        albums.length > 0
                        ?
                        (
                          albums.map((album) => (
                            <li key={ album.id }>
                              <div className="commonForm">
                                <div className="formRow fullWidth">
                                  <label htmlFor={`album-${ album.id }`} className="fieldBox checkBox">
                                    <input type="checkbox" id={`album-${ album.id }`} name="eachAlbum" onChange={(e) => { setLoadingAlbums((prev) => ({ ...prev, [album.artistId]: true }));checkAlbum(e.target.checked, album); } } />
                                    <label htmlFor={`album-${ album.id }`}>
                                      <img src={ album.cover } alt="앨범 커버" />
                                      <p>앨범명 : { album.title }</p>
                                      <p>발매일 : { album.year }</p>
                                      <p>아티스트 : { album.artist }</p>
                                      { album.description && <p className={ styleClasses.description }>설명 : { album.description }</p> }
                                    </label>
                                  </label>
                                </div>
                              </div>
                            </li>
                          ))
                        )
                        :
                        (
                          <p className={ styleClasses.noResults }>앨범 결과가 없습니다.</p>
                        )
                      }
                      </ul>
                    ))
                  }
                  </>
                )
                :
                null
              }
              </div>
            </>
          }
          </div>

          {/* Toast - 앨범 가져올 때 로딩 표시 */}
          <div className={ styleClasses.toast }>
            <div className={ styleClasses.fetchingToast }>
            {
              selectedResults.map((result) => (
                loadingAlbums[result.artistId]
                ?
                <div className={ styleClasses.fetchingAblum }>
                  <div className="commonIcon">
                    { result.artistName } 앨범을 가져오는 중입니다.
                    <i className="loaderBlack w24"></i>
                  </div>
                </div>
                :
                null
              ))
            }
            </div>
          </div>

          {/* Footer */}
          {
            !loading
            &&
            (
              showProgress
              ?
              <div className={ styleClasses.progressBarDiv }>
                <div className={ styleClasses.progressBar }>
                  <div className={ styleClasses.progress }>
                    <div className={ styleClasses.inProgress } style={{ width: `${ progress }%` }}></div>
                    <div className={ styleClasses.percent}>
                      <span>{`${ progress }%`}</span>
                    </div>
                  </div>
                </div>
              </div>
              :
              <div className={ styleClasses.footer }>
                <div className={`commonButton fullWidth ${ styleClasses.w160 }`}>
                  <button onClick={ () => { setActive(''); setOpenPopup(false); } }>닫기</button>
                </div>
                <div className={`commonButton fullWidth ${ styleClasses.w160 }`}>
                  <button className="black" onClick={ handleComplete }>완료</button>
                </div>
              </div>
            )
          }
        </div>
      </div>
      <Popup open={ openInnerPopup } setOpenPopup={ setOpenInnerPopup } title={ innerPopupTitle } text={ innerPopupText } />
    </>
  );
}

export default ImportUnreleasedArtistPopup;